/**
 * "בדיקת רכב יד 2" — public lookup card on the home screen + result modal.
 *
 * Renders a violet/purple hero card with an inline plate input (mirrors
 * the disabledBadge layout). Submission triggers `analyzeUsedCar` and
 * opens a modal with:
 *   - Plate badge
 *   - Red flags banner (if any)
 *   - Ownership card (count + avg years per owner + timeline)
 *   - Usage card (private / rental / leasing / taxi)
 *   - Recalls card (open recalls with manufacturer links)
 *   - License status (valid until + days left + last test)
 *   - Technical details accordion (full gov record)
 *   - External-link buttons (רשם המשכונות for liens)
 *
 * Public data — no auth required.
 */

import { analyzeUsedCar } from './analyze.js';
import { normalize as normalizePlate, isValid as isValidPlate, format as formatPlate } from '../../shared/plate.js';
import { escape } from '../../shared/escape.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('usedCarCheck.view');

const CARD_ID = 'usedCarCheckCard';
const INPUT_ID = 'usedCarInput';
const BUTTON_ID = 'usedCarBtn';
const ERROR_ID = 'usedCarErr';
const OVERLAY_ID = 'usedCarOverlay';

// ====================================================================
// Public API
// ====================================================================

export function renderCard() {
  ensureCardStyles();

  const el = document.createElement('div');
  el.id = CARD_ID;
  el.className = 'usedcar-hero b-row-full';
  el.innerHTML = `
    <div class="usedcar-hero-top">
      <div class="usedcar-hero-icon" aria-hidden="true">🔍</div>
      <div class="usedcar-hero-body">
        <div class="usedcar-hero-title">בדיקת רכב לפני קנייה</div>
        <div class="usedcar-hero-sub">לקראת רכישת יד 2? בדוק מה רשום עליו</div>
      </div>
    </div>
    <div class="usedcar-hero-form">
      <input
        id="${INPUT_ID}"
        type="text"
        inputmode="numeric"
        pattern="[0-9]*"
        autocomplete="off"
        maxlength="10"
        placeholder="מספר רכב"
        aria-label="מספר רכב לבדיקה"
        dir="ltr"
      />
      <button id="${BUTTON_ID}" type="button" disabled>בדוק</button>
    </div>
    <div id="${ERROR_ID}" class="usedcar-hero-err" role="alert" aria-live="polite"></div>
  `;

  const input = /** @type {HTMLInputElement} */ (el.querySelector('#' + INPUT_ID));
  const button = /** @type {HTMLButtonElement} */ (el.querySelector('#' + BUTTON_ID));
  const errEl = /** @type {HTMLElement} */ (el.querySelector('#' + ERROR_ID));

  input.addEventListener('input', () => {
    const digits = normalizePlate(input.value);
    const formatted = digits.length >= 4 ? formatPlate(digits) : digits;
    if (formatted !== input.value) input.value = formatted;
    button.disabled = !isValidPlate(digits);
    if (errEl.textContent) errEl.textContent = '';
  });

  input.addEventListener('keydown', (e) => {
    const ke = /** @type {KeyboardEvent} */ (e);
    if (ke.key === 'Enter' && !button.disabled) {
      ke.preventDefault();
      button.click();
    }
  });

  button.addEventListener('click', () => {
    const digits = normalizePlate(input.value);
    if (!isValidPlate(digits)) {
      errEl.textContent = 'יש להזין 7 או 8 ספרות.';
      return;
    }
    errEl.textContent = '';
    void openModal(digits);
  });

  return el;
}

/** @param {string} rawPlate */
export async function openModal(rawPlate) {
  const digits = normalizePlate(rawPlate);
  if (!isValidPlate(digits)) {
    log.warn('openModal called with invalid plate:', rawPlate);
    return;
  }

  ensureModalStyles();
  const overlay = ensureModal();
  document.body.appendChild(overlay);
  const body = /** @type {HTMLElement} */ (overlay.querySelector('#usedCarBody'));

  body.innerHTML = `
    <div class="usedcar-modal-loading">
      <div class="usedcar-modal-plate-wrap">
        <span class="usedcar-modal-plate-label">בודק את הרכב</span>
        <span class="usedcar-modal-plate">${escape(formatPlate(digits))}</span>
      </div>
      <div class="usedcar-modal-spinner" aria-hidden="true"></div>
      <div class="usedcar-modal-loading-text">אוסף נתונים מ־5 מאגרים ציבוריים...</div>
    </div>
  `;
  overlay.classList.add('open');

  /** @type {import('./analyze.js').ReportResult} */
  let report;
  try {
    report = await analyzeUsedCar(digits);
  } catch (err) {
    log.error('analyze failed:', err);
    body.innerHTML = `
      <div class="usedcar-result usedcar-result-error">
        <div class="usedcar-result-icon">⚠️</div>
        <div class="usedcar-result-headline">לא הצלחנו לבדוק כרגע</div>
        <div class="usedcar-result-sub">המאגרים אינם זמינים. נסה שוב בעוד רגע.</div>
      </div>
    `;
    return;
  }

  body.innerHTML = renderReport(report);
  wireReportInteractions(body, report);
}

export function close() {
  const overlay = document.getElementById(OVERLAY_ID);
  if (overlay) overlay.classList.remove('open');
}

// ====================================================================
// Modal DOM
// ====================================================================

function ensureModal() {
  let overlay = document.getElementById(OVERLAY_ID);
  if (overlay) return overlay;
  overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.className = 'usedcar-modal-overlay';
  overlay.innerHTML = `
    <aside class="usedcar-modal" role="dialog" aria-modal="true" aria-labelledby="usedCarTitle">
      <header class="usedcar-modal-header">
        <div id="usedCarTitle" class="usedcar-modal-title">בדיקת רכב יד 2</div>
        <button class="usedcar-modal-close" id="usedCarCloseBtn" type="button" aria-label="סגור">✕</button>
      </header>
      <div id="usedCarBody" class="usedcar-modal-body"></div>
      <footer class="usedcar-modal-footer">
        <button class="usedcar-modal-ok" id="usedCarOkBtn" type="button">סגור</button>
      </footer>
    </aside>
  `;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector('#usedCarCloseBtn')?.addEventListener('click', close);
  overlay.querySelector('#usedCarOkBtn')?.addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    const ke = /** @type {KeyboardEvent} */ (e);
    if (ke.key === 'Escape' && overlay?.classList.contains('open')) close();
  });
  return overlay;
}

// ====================================================================
// Report rendering
// ====================================================================

/** @param {import('./analyze.js').ReportResult} report */
function renderReport(report) {
  if (!report.found) {
    return `
      <div class="usedcar-modal-plate-wrap">
        <span class="usedcar-modal-plate-label">מספר רכב</span>
        <span class="usedcar-modal-plate">${escape(formatPlate(report.plate))}</span>
      </div>
      <div class="usedcar-result usedcar-result-unknown">
        <div class="usedcar-result-icon">🤷</div>
        <div class="usedcar-result-headline">לא נמצא רכב עם מספר זה</div>
        <div class="usedcar-result-sub">בדוק שהקלדת נכון, או שייתכן שהרכב חדש ב־CKAN.</div>
      </div>
    `;
  }

  const { plate, vehicle, ownership, license, recalls, redFlags } = report;
  return `
    <div class="usedcar-modal-plate-wrap">
      <span class="usedcar-modal-plate-label">מספר רכב</span>
      <span class="usedcar-modal-plate">${escape(formatPlate(plate))}</span>
    </div>

    ${renderVehicleHeader(vehicle)}
    ${renderRedFlagsBanner(redFlags)}
    ${renderOwnershipCard(ownership)}
    ${renderLicenseCard(license)}
    ${renderRecallsCard(recalls)}
    ${renderTechAccordion(vehicle)}
    ${renderExternalActions(plate)}
    ${renderDisclaimer()}
  `;
}

/** @param {any} v */
function renderVehicleHeader(v) {
  const manufacturer = String(v?.tozeret_nm || '').trim();
  const commercial = String(v?.kinuy_mishari || v?.degem_nm || '').trim();
  const fullModel = [manufacturer, commercial].filter(Boolean).join(' ') || 'רכב';
  const year = v?.shnat_yitzur || '';
  const color = String(v?.tzeva_rechev || '').trim();
  return `
    <div class="usedcar-veh-header">
      <div class="usedcar-veh-model">${escape(fullModel)}</div>
      <div class="usedcar-veh-meta">${year ? `שנת ${escape(String(year))}` : ''}${year && color ? ' · ' : ''}${escape(color)}</div>
    </div>
  `;
}

/** @param {import('./analyze.js').RedFlag[]} flags */
function renderRedFlagsBanner(flags) {
  if (flags.length === 0) {
    return `
      <div class="usedcar-flags usedcar-flags-clean">
        <div class="usedcar-flags-icon">✅</div>
        <div class="usedcar-flags-text">
          <strong>אין סימני אזהרה בנתונים שבדקנו</strong>
          <span>מבדיקה ראשונית של הנתונים הציבוריים — לא עלו דגלים אדומים. עדיין מומלץ לבדיקת מוסך פיזית.</span>
        </div>
      </div>
    `;
  }
  const highCount = flags.filter((f) => f.severity === 'high').length;
  const headline = highCount > 0
    ? `${highCount} סימני אזהרה חמורים`
    : `${flags.length} סימני שימת לב`;
  return `
    <div class="usedcar-flags usedcar-flags-warn">
      <div class="usedcar-flags-headline">
        <span class="usedcar-flags-icon">${highCount > 0 ? '🚨' : '⚠️'}</span>
        <span>${escape(headline)}</span>
      </div>
      <ul class="usedcar-flags-list">
        ${flags.map((f) => `
          <li class="usedcar-flag usedcar-flag-${escape(f.severity)}">${escape(f.text)}</li>
        `).join('')}
      </ul>
    </div>
  `;
}

/** @param {import('./analyze.js').OwnershipSummary} o */
function renderOwnershipCard(o) {
  if (o.count === 0) {
    return `
      <div class="usedcar-card">
        <div class="usedcar-card-title">👥 בעלות</div>
        <div class="usedcar-card-body">לא נמצאו רשומות העברות בעלות. ייתכן שזה הבעלים המקורי.</div>
      </div>
    `;
  }
  const avg = o.avgYearsPerOwner !== null
    ? `~${Math.round(o.avgYearsPerOwner * 10) / 10} שנים בממוצע לבעלים`
    : '';
  return `
    <div class="usedcar-card">
      <div class="usedcar-card-title">👥 בעלות</div>
      <div class="usedcar-card-body">
        <div class="usedcar-stat-big">${o.count} <span>בעלים רשומים</span></div>
        ${avg ? `<div class="usedcar-stat-sub">${escape(avg)}</div>` : ''}
        ${o.timeline.length > 0 ? `
          <details class="usedcar-timeline-toggle">
            <summary>הצג ציר זמן (${o.timeline.length})</summary>
            <ul class="usedcar-timeline">
              ${o.timeline.slice().reverse().map((t) => `
                <li>
                  <span class="usedcar-timeline-date">${escape(t.date)}</span>
                  <span class="usedcar-timeline-baalut">${escape(translateBaalut(t.baalut))}</span>
                </li>
              `).join('')}
            </ul>
          </details>
        ` : ''}
      </div>
    </div>
  `;
}

/** @param {import('./analyze.js').LicenseSummary} l */
function renderLicenseCard(l) {
  if (!l.validUntil) {
    return `
      <div class="usedcar-card">
        <div class="usedcar-card-title">📅 רישוי</div>
        <div class="usedcar-card-body">תאריך תוקף רישוי לא זמין במאגר.</div>
      </div>
    `;
  }
  const days = l.daysLeft ?? 0;
  let statusHtml;
  if (days < 0) {
    statusHtml = `<div class="usedcar-status usedcar-status-bad">פג לפני ${-days} ימים</div>`;
  } else if (days < 30) {
    statusHtml = `<div class="usedcar-status usedcar-status-warn">תקף עוד ${days} ימים</div>`;
  } else {
    statusHtml = `<div class="usedcar-status usedcar-status-ok">תקף עוד ${days} ימים</div>`;
  }
  return `
    <div class="usedcar-card">
      <div class="usedcar-card-title">📅 רישוי</div>
      <div class="usedcar-card-body">
        ${statusHtml}
        <div class="usedcar-stat-sub">תוקף עד ${escape(l.validUntil)}</div>
        ${l.lastTestDate ? `<div class="usedcar-stat-sub">טסט אחרון: ${escape(l.lastTestDate)}</div>` : ''}
      </div>
    </div>
  `;
}

/** @param {Array<{ id: string; desc: string; web: string }>} recalls */
function renderRecallsCard(recalls) {
  if (recalls.length === 0) {
    return `
      <div class="usedcar-card">
        <div class="usedcar-card-title">🔔 קריאות יצרן</div>
        <div class="usedcar-card-body">
          <div class="usedcar-status usedcar-status-ok">אין קריאות יצרן פתוחות לרכב זה</div>
        </div>
      </div>
    `;
  }
  return `
    <div class="usedcar-card usedcar-card-alert">
      <div class="usedcar-card-title">🔔 קריאות יצרן פתוחות (${recalls.length})</div>
      <div class="usedcar-card-body">
        <ul class="usedcar-recalls">
          ${recalls.slice(0, 5).map((r) => `
            <li>
              <div class="usedcar-recall-desc">${escape(r.desc.slice(0, 200))}</div>
              ${r.web ? `<a href="${escape(r.web)}" target="_blank" rel="noopener" class="usedcar-recall-link">פתח אתר היצרן ↗</a>` : ''}
            </li>
          `).join('')}
        </ul>
        ${recalls.length > 5 ? `<div class="usedcar-stat-sub">+${recalls.length - 5} נוספות</div>` : ''}
      </div>
    </div>
  `;
}

/** @param {any} v */
function renderTechAccordion(v) {
  /** @type {Array<[string, string]>} */
  const rows = [];
  if (v.tozeret_nm)     rows.push(['יצרן',          String(v.tozeret_nm)]);
  if (v.kinuy_mishari)  rows.push(['דגם מסחרי',     String(v.kinuy_mishari)]);
  if (v.degem_nm)       rows.push(['דגם יצרן',       String(v.degem_nm)]);
  if (v.shnat_yitzur)   rows.push(['שנת ייצור',     String(v.shnat_yitzur)]);
  if (v.sug_delek_nm)   rows.push(['דלק',             String(v.sug_delek_nm)]);
  if (v.degem_manoa)    rows.push(['קוד מנוע',        String(v.degem_manoa)]);
  if (v.zmig_kidmi)     rows.push(['צמיג קדמי',     String(v.zmig_kidmi)]);
  if (v.zmig_ahori)     rows.push(['צמיג אחורי',     String(v.zmig_ahori)]);
  if (v.kvutzat_zihum)  rows.push(['קבוצת זיהום', String(v.kvutzat_zihum)]);
  if (v.ramat_gimur)    rows.push(['רמת גימור',     String(v.ramat_gimur)]);
  if (v.ramat_eivzur_betihuty) rows.push(['בטיחות', String(v.ramat_eivzur_betihuty)]);
  if (v.misgeret)       rows.push(['מספר שלדה',     String(v.misgeret)]);
  if (rows.length === 0) return '';
  return `
    <details class="usedcar-tech">
      <summary>📋 כל הפרטים הטכניים</summary>
      <dl class="usedcar-tech-list">
        ${rows.map(([k, val]) => `
          <div class="usedcar-tech-row">
            <dt>${escape(k)}</dt>
            <dd>${escape(val)}</dd>
          </div>
        `).join('')}
      </dl>
    </details>
  `;
}

/** @param {string} plate */
function renderExternalActions(plate) {
  return `
    <div class="usedcar-actions">
      <div class="usedcar-actions-title">בדיקות נוספות מומלצות:</div>
      <button class="usedcar-action-btn" data-act="liens">
        🔒 בדוק שעבוד ב־רשם המשכונות (₪12)
        <span class="usedcar-action-sub">פתיחת אתר רשם המשכונות עם מספר הרכב</span>
      </button>
    </div>
  `;
}

function renderDisclaimer() {
  return `
    <div class="usedcar-disclaimer">
      <strong>⚠️ הבהרה חשובה:</strong> בדיקה זו מבוססת על נתונים ציבוריים בלבד.
      <strong>היסטוריית תאונות וקילומטראז' אינן זמינות בישראל</strong> מטעמי פרטיות.
      בדיקה במוסך מקצועי חיונית לפני קנייה.
    </div>
  `;
}

/**
 * @param {HTMLElement} body
 * @param {import('./analyze.js').ReportResult} report
 */
function wireReportInteractions(body, report) {
  if (!report.found) return;
  const liensBtn = body.querySelector('[data-act="liens"]');
  liensBtn?.addEventListener('click', () => {
    // רשם המשכונות (Ministry of Justice corporate registry / pledges) —
    // the official search interface at ica.justice.gov.il. Costs ₪10 per
    // check; requires national-ID login. URL doesn't accept query params
    // for plate, so we copy it to clipboard for the user to paste.
    try { navigator.clipboard?.writeText(report.plate); } catch (_) { /* ignore */ }
    const url = 'https://ica.justice.gov.il/SearchPledge/PledgeBrowse';
    window.open(url, '_blank', 'noopener');
  });
}

/** Translate gov bealut codes to Hebrew labels for the timeline view. */
function translateBaalut(code) {
  if (!code) return '—';
  const c = code.toUpperCase();
  /** @type {Record<string, string>} */
  const map = {
    PRIV: 'פרטי', LEAS: 'ליסינג', RENT: 'השכרה', TAXI: 'מונית',
    COMP: 'חברה',  GOV:  'ממשלתי',  IMPO: 'יבוא',   POL:  'משטרה',
  };
  return map[c] || code;
}

// ====================================================================
// Styles — injected once
// ====================================================================

let __cardStylesInjected = false;
function ensureCardStyles() {
  if (__cardStylesInjected) return;
  __cardStylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    /* Violet gradient — distinct from green disabled-badge and indigo
       AI-diagnostic so the three home heroes read as separate features. */
    .usedcar-hero {
      position: relative;
      padding: 16px 16px 18px;
      background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 50%, #6d28d9 100%);
      color: #fff;
      border-radius: 18px;
      overflow: hidden;
      box-shadow: 0 8px 24px rgba(139, 92, 246, 0.30);
    }
    .usedcar-hero::before {
      content: '';
      position: absolute;
      top: -20px; right: -20px;
      width: 120px; height: 120px;
      background: radial-gradient(circle, rgba(255,255,255,0.18) 0%, transparent 70%);
      pointer-events: none;
    }
    .usedcar-hero-top {
      display: flex; align-items: center; gap: 12px; margin-bottom: 14px;
    }
    .usedcar-hero-icon {
      flex-shrink: 0; width: 48px; height: 48px;
      background: rgba(255,255,255,0.22);
      border-radius: 13px;
      display: inline-flex; align-items: center; justify-content: center;
      font-size: 26px;
    }
    .usedcar-hero-body { flex: 1; min-width: 0; }
    .usedcar-hero-title {
      font-family: 'Rubik', sans-serif;
      font-weight: 900; font-size: 16px;
      margin-bottom: 3px; letter-spacing: -0.01em;
    }
    .usedcar-hero-sub { font-size: 12px; opacity: 0.92; line-height: 1.4; }
    .usedcar-hero-form { display: flex; gap: 8px; align-items: stretch; }
    .usedcar-hero-form input {
      flex: 1; min-width: 0;
      padding: 11px 14px;
      border: none; border-radius: 12px;
      background: rgba(255,255,255,0.96);
      color: #0f172a;
      font-family: 'Rubik', sans-serif;
      font-size: 16px; font-weight: 700;
      letter-spacing: 0.04em; text-align: center;
      outline: none;
      box-shadow: 0 2px 6px rgba(0,0,0,0.12) inset;
    }
    .usedcar-hero-form input::placeholder {
      color: #94a3b8; font-weight: 500; letter-spacing: 0;
    }
    .usedcar-hero-form input:focus {
      box-shadow: 0 0 0 3px rgba(255,255,255,0.45);
    }
    .usedcar-hero-form button {
      flex-shrink: 0; padding: 0 18px;
      border: none; border-radius: 12px;
      background: rgba(255,255,255,0.96);
      color: #6d28d9;
      font-family: 'Rubik', sans-serif;
      font-weight: 900; font-size: 14px;
      cursor: pointer;
      transition: transform 0.12s, opacity 0.18s;
    }
    .usedcar-hero-form button:disabled { opacity: 0.55; cursor: not-allowed; }
    .usedcar-hero-form button:not(:disabled):hover { transform: translateY(-1px); }
    .usedcar-hero-err {
      min-height: 0; margin-top: 6px;
      font-size: 12px; color: #fef3c7; font-weight: 600;
    }
    .usedcar-hero-err:empty { margin-top: 0; }
  `;
  document.head.appendChild(style);
}

let __modalStylesInjected = false;
function ensureModalStyles() {
  if (__modalStylesInjected) return;
  __modalStylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    .usedcar-modal-overlay {
      position: fixed; inset: 0;
      background: rgba(15, 23, 42, 0.55);
      backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
      z-index: 99998;
      display: none;
      align-items: center; justify-content: center;
      padding: 20px;
      font-family: 'Rubik', 'Heebo', sans-serif;
    }
    .usedcar-modal-overlay.open { display: flex; }
    .usedcar-modal {
      background: #fff;
      border-radius: 20px;
      width: 100%; max-width: 480px; max-height: 92vh;
      display: flex; flex-direction: column;
      direction: rtl; overflow: hidden;
      box-shadow: 0 30px 80px rgba(0,0,0,0.40);
      animation: usedcar-pop 0.22s ease-out;
    }
    @keyframes usedcar-pop {
      from { transform: scale(0.94); opacity: 0; }
      to   { transform: scale(1);    opacity: 1; }
    }
    .usedcar-modal-header {
      display: flex; align-items: center; justify-content: space-between;
      padding: 16px 18px;
      border-bottom: 1px solid #e2e8f0;
    }
    .usedcar-modal-title { font-weight: 800; font-size: 16px; color: #0f172a; }
    .usedcar-modal-close {
      background: transparent; border: none;
      font-size: 22px; color: #64748b;
      cursor: pointer; line-height: 1; padding: 4px 8px;
    }
    .usedcar-modal-close:hover { color: #0f172a; }
    .usedcar-modal-body { padding: 18px; overflow-y: auto; flex: 1; }
    .usedcar-modal-footer {
      padding: 12px 18px 16px;
      border-top: 1px solid #e2e8f0;
      display: flex; justify-content: center;
    }
    .usedcar-modal-ok {
      padding: 11px 36px;
      border: none; border-radius: 999px;
      background: linear-gradient(135deg, #8b5cf6, #6d28d9);
      color: #fff; font-family: inherit;
      font-weight: 800; font-size: 14px;
      cursor: pointer;
      box-shadow: 0 6px 16px rgba(139, 92, 246, 0.35);
    }
    .usedcar-modal-plate-wrap {
      display: flex; flex-direction: column; align-items: center;
      gap: 4px; margin-bottom: 16px;
    }
    .usedcar-modal-plate-label {
      font-size: 11px; color: #64748b; font-weight: 600;
      letter-spacing: 0.05em; text-transform: uppercase;
    }
    .usedcar-modal-plate {
      display: inline-block;
      background: #fef3c7; color: #0f172a;
      padding: 8px 16px; border-radius: 8px;
      font-family: 'Courier New', monospace;
      font-weight: 900; font-size: 22px;
      letter-spacing: 0.08em;
      border: 2px solid #f59e0b;
      direction: ltr;
    }

    .usedcar-modal-loading {
      display: flex; flex-direction: column; align-items: center;
      gap: 14px; padding: 20px 0 30px;
    }
    .usedcar-modal-spinner {
      width: 34px; height: 34px;
      border: 3px solid #e2e8f0;
      border-top-color: #8b5cf6;
      border-radius: 50%;
      animation: usedcar-spin 0.8s linear infinite;
    }
    @keyframes usedcar-spin { to { transform: rotate(360deg); } }
    .usedcar-modal-loading-text { color: #64748b; font-size: 13px; }

    .usedcar-result {
      padding: 22px 18px; border-radius: 14px;
      text-align: center; margin-bottom: 18px;
    }
    .usedcar-result-icon { font-size: 38px; margin-bottom: 8px; line-height: 1; }
    .usedcar-result-headline { font-weight: 900; font-size: 20px; margin-bottom: 6px; }
    .usedcar-result-sub { font-size: 13px; opacity: 0.85; line-height: 1.5; }
    .usedcar-result-unknown { background: linear-gradient(135deg,#fef3c7,#fde68a); color: #78350f; }
    .usedcar-result-error   { background: linear-gradient(135deg,#fee2e2,#fecaca); color: #991b1b; }

    .usedcar-veh-header {
      text-align: center; margin-bottom: 18px;
      padding-bottom: 14px; border-bottom: 1px dashed #e2e8f0;
    }
    .usedcar-veh-model { font-size: 18px; font-weight: 800; color: #0f172a; }
    .usedcar-veh-meta { font-size: 13px; color: #64748b; margin-top: 4px; }

    .usedcar-flags {
      padding: 14px 16px; border-radius: 12px; margin-bottom: 14px;
    }
    .usedcar-flags-clean {
      background: linear-gradient(135deg,#d1fae5,#a7f3d0);
      color: #065f46;
      display: flex; gap: 12px; align-items: flex-start;
    }
    .usedcar-flags-clean .usedcar-flags-icon { font-size: 24px; line-height: 1; }
    .usedcar-flags-clean .usedcar-flags-text { display: flex; flex-direction: column; gap: 3px; font-size: 13px; }
    .usedcar-flags-clean strong { font-weight: 800; font-size: 14px; }
    .usedcar-flags-warn {
      background: linear-gradient(135deg,#fee2e2,#fecaca);
      color: #7f1d1d;
    }
    .usedcar-flags-headline {
      display: flex; align-items: center; gap: 8px;
      font-weight: 800; font-size: 15px; margin-bottom: 10px;
    }
    .usedcar-flags-list { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 8px; }
    .usedcar-flag {
      padding: 9px 12px; border-radius: 8px;
      font-size: 13px; line-height: 1.5;
      background: rgba(255,255,255,0.6);
    }
    .usedcar-flag-high   { border-right: 3px solid #dc2626; }
    .usedcar-flag-medium { border-right: 3px solid #f59e0b; }
    .usedcar-flag-low    { border-right: 3px solid #94a3b8; }

    .usedcar-card {
      background: #f8fafc; border-radius: 12px;
      padding: 14px 16px; margin-bottom: 12px;
    }
    .usedcar-card-alert { background: #fef2f2; border: 1px solid #fecaca; }
    .usedcar-card-title {
      font-weight: 800; font-size: 13px;
      color: #475569; margin-bottom: 8px;
    }
    .usedcar-card-alert .usedcar-card-title { color: #991b1b; }
    .usedcar-card-body { font-size: 13px; color: #0f172a; line-height: 1.5; }
    .usedcar-stat-big {
      font-size: 28px; font-weight: 900; color: #0f172a;
      letter-spacing: -0.02em; line-height: 1.1;
    }
    .usedcar-stat-big span { font-size: 13px; font-weight: 600; color: #64748b; }
    .usedcar-stat-sub { font-size: 12px; color: #64748b; margin-top: 4px; }
    .usedcar-status {
      display: inline-block; padding: 5px 12px;
      border-radius: 999px; font-weight: 700; font-size: 13px;
    }
    .usedcar-status-ok   { background: #d1fae5; color: #065f46; }
    .usedcar-status-warn { background: #fef3c7; color: #78350f; }
    .usedcar-status-bad  { background: #fee2e2; color: #991b1b; }

    .usedcar-timeline-toggle { margin-top: 10px; }
    .usedcar-timeline-toggle summary {
      cursor: pointer; font-size: 12px;
      color: #6d28d9; font-weight: 700;
      list-style: none;
    }
    .usedcar-timeline-toggle summary::-webkit-details-marker { display: none; }
    .usedcar-timeline-toggle summary::before { content: '▸ '; }
    .usedcar-timeline-toggle[open] summary::before { content: '▾ '; }
    .usedcar-timeline {
      margin: 10px 0 0; padding: 0; list-style: none;
      display: flex; flex-direction: column; gap: 6px;
    }
    .usedcar-timeline li {
      display: flex; justify-content: space-between;
      padding: 6px 10px; background: #fff;
      border-radius: 6px; font-size: 12px;
    }
    .usedcar-timeline-date { color: #64748b; font-family: 'Courier New', monospace; }
    .usedcar-timeline-baalut { color: #0f172a; font-weight: 700; }

    .usedcar-recalls { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 10px; }
    .usedcar-recalls li {
      padding: 10px 12px;
      background: #fff;
      border: 1px solid #fecaca;
      border-radius: 8px;
    }
    .usedcar-recall-desc { font-size: 12.5px; color: #0f172a; line-height: 1.45; margin-bottom: 6px; }
    .usedcar-recall-link {
      display: inline-block; padding: 5px 10px;
      background: linear-gradient(135deg,#dc2626,#991b1b);
      color: #fff;
      border-radius: 6px;
      text-decoration: none;
      font-size: 11.5px; font-weight: 700;
    }

    .usedcar-tech { margin-bottom: 14px; }
    .usedcar-tech summary {
      cursor: pointer;
      padding: 10px 14px;
      background: #f1f5f9; border-radius: 10px;
      font-weight: 700; font-size: 13px;
      color: #475569;
      list-style: none;
    }
    .usedcar-tech summary::-webkit-details-marker { display: none; }
    .usedcar-tech summary::after { content: ' ▾'; opacity: 0.5; }
    .usedcar-tech[open] summary::after { content: ' ▴'; }
    .usedcar-tech-list {
      margin: 8px 0 0; padding: 0;
      display: flex; flex-direction: column; gap: 5px;
    }
    .usedcar-tech-row {
      display: flex; justify-content: space-between; align-items: center;
      font-size: 12.5px; padding: 6px 14px;
    }
    .usedcar-tech-row dt { color: #64748b; font-weight: 600; margin: 0; }
    .usedcar-tech-row dd { color: #0f172a; font-weight: 700; margin: 0; }

    .usedcar-actions { margin-bottom: 14px; }
    .usedcar-actions-title { font-size: 12px; color: #475569; margin-bottom: 8px; font-weight: 700; }
    .usedcar-action-btn {
      width: 100%; text-align: right;
      padding: 12px 14px;
      background: linear-gradient(135deg,#f1f5f9,#e2e8f0);
      border: 1px solid #cbd5e1; border-radius: 10px;
      font-family: inherit; cursor: pointer;
      display: flex; flex-direction: column; gap: 3px;
      transition: transform 0.12s;
    }
    .usedcar-action-btn:hover { transform: translateY(-1px); background: linear-gradient(135deg,#e2e8f0,#cbd5e1); }
    .usedcar-action-btn > :first-child { font-weight: 800; font-size: 13.5px; color: #0f172a; }
    .usedcar-action-sub { font-size: 11.5px; color: #64748b; font-weight: 500; }

    .usedcar-disclaimer {
      margin-top: 14px;
      padding: 12px 14px;
      background: #fff7ed;
      border: 1px solid #fed7aa;
      border-radius: 10px;
      font-size: 11.5px;
      color: #7c2d12;
      line-height: 1.55;
    }
    .usedcar-disclaimer strong { font-weight: 800; }
  `;
  document.head.appendChild(style);
}
